import { Service, Services } from "@/models/Service";
import { makeStyles, mergeClasses, Text, tokens } from "@fluentui/react-components";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { PlainTextPlugin } from "@lexical/react/LexicalPlainTextPlugin";
import { TextNode } from "lexical";
import React, { useEffect, useRef, useState } from "react";
import { CaptureTextChangePlugin } from "./CaptureTextChangePlugin";
import { DisablePlugin } from "./DisablePlugin";
import { MentionNode } from "./MentionNode";
import { MentionPlugin } from "./MentionPlugin";
import SendButtonPlugin from "./SendButtonPlugin";
import { SendOnEnterPlugin } from "./SendOnEnterPlugin";

const editorConfig = {
  namespace: "PlainTextMention",
  onError: console.error,
  nodes: [MentionNode, TextNode],
};

type Props = {
  onSendMessage: (message: string, recipientServiceId?: string) => void;
  services?: Services;
  defaultRecipientId?: string;
  recipientId?: string;
  disabled?: boolean;
};

export const CustomInputField: React.FC<Props> = (
  { onSendMessage, services, defaultRecipientId, recipientId, disabled = false },
) => {
  const classes = useStyles();
  const editorRef = useRef<HTMLDivElement>(null);
  const [selectedRecipientId, setSelectedRecipientId] = useState<string | undefined>(recipientId);
  const [selectedRecipient, setSelectedRecipient] = useState<Service | undefined>(undefined);

  const [inputValue, setInputValue] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

  // Track the previous recipientId to avoid unnecessary updates
  const prevRecipientIdRef = useRef<string | undefined>(recipientId);

  useEffect(() => {
    // Skip the effect if recipientId hasn't actually changed
    if (recipientId && recipientId !== prevRecipientIdRef.current) {
      prevRecipientIdRef.current = recipientId;

      setSelectedRecipientId(recipientId);
      if (services) {
        const selectedService = services.find(service => service.id === recipientId);
        if (selectedService) {
          setSelectedRecipient(selectedService);
        }
      }
    }
  }, [recipientId, services]);

  const handleServiceSelect = (newRecipientId: string | undefined) => {
    if (!newRecipientId || newRecipientId === selectedRecipientId) return;
    setErrorMessage(undefined);

    setSelectedRecipientId(newRecipientId);
  };

  const handleSend = () => {
    if (inputValue === "") {
      return;
    }
    const targetService = selectedRecipientId ?? defaultRecipientId;
    if (!targetService) {
      setErrorMessage("質問を送るチャットが選択されていません。");
      return;
    }
    onSendMessage(inputValue, selectedRecipientId);
    setInputValue("");
    setSelectedRecipientId(undefined);
  };

  return (
    <div className={classes.container}>
      <LexicalComposer initialConfig={editorConfig}>
        <div className={classes.editorContainer} ref={editorRef}>
          <PlainTextPlugin
            contentEditable={
              <ContentEditable
                className={classes.contentEditable}
              />
            }
            placeholder={
              <div className={mergeClasses(classes.editorPlaceholder, disabled ? "disabled" : "")}>
                質問を入力してください。質問を文章で書くと、より早く正確な回答ができます。
              </div>
            }
            ErrorBoundary={(e) => <>{console.log(e)}</>}
          />
          {/* Using the improved MentionPlugin instead of MentionAutoInsertPlugin */}
          <SendOnEnterPlugin onSend={handleSend} />
          <MentionPlugin
            services={services}
            editorRef={editorRef}
            onMentionChange={handleServiceSelect}
            selectedService={selectedRecipient}
          />
          <CaptureTextChangePlugin onChange={setInputValue} />
          <DisablePlugin disabled={disabled} />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <SendButtonPlugin onClick={handleSend} disabled={disabled} />
        </div>
      </LexicalComposer>
      {errorMessage && <Text className={classes.errorMessage}>{errorMessage}</Text>}
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    position: "relative",
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },
  editorContainer: {
    position: "relative",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    width: "100%",
  },
  contentEditable: {
    position: "relative",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL}`,
    paddingRight: "48px", // ボタンのスペースを開けるため上書き
    // Set max height for 3 lines and enable vertical scrolling
    maxHeight: "calc(4.5em)", // Approximately 3 lines (1.5em per line)
    minHeight: "1.5em", // At least one line
    overflowY: "auto",
    // Target the paragraph elements created by Lexical
    "& p": {
      margin: 0,
      padding: 0,
      lineHeight: "1.5em",
    },
    // Additional selectors to ensure no margins in contenteditable
    "& div": {
      margin: 0,
      padding: 0,
    },
  },
  editorPlaceholder: {
    color: tokens.colorNeutralForeground3,
    position: "absolute",
    top: tokens.spacingVerticalS,
    left: tokens.spacingHorizontalL,
    userSelect: "none",
    pointerEvents: "none",
    // disableの時は色を替える
    "&.disabled": {
      color: tokens.colorNeutralForegroundDisabled,
    },
  },
  errorMessage: {
    color: tokens.colorStatusDangerForeground1,
    fontSize: tokens.fontSizeBase200,
    marginTop: tokens.spacingVerticalXXS,
    marginLeft: tokens.spacingHorizontalL,
  },
});
